import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import PrivateRoute from './components/PrivateRoute';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import TestList from './pages/TestList';
import TestEntryPage from './pages/TestEntryPage';
import Results from './pages/Results';
import AdminPanel from './pages/AdminPanel';
import AITestGenerator from './pages/AITestGenerator';
import TestQuestions from './pages/TestQuestions';
import AutoBrandBuilder from './pages/AutoBrandBuilder';
import VideoPortal from './pages/VideoPortal';
import VideoFolder from './pages/VideoFolder';
import VideoPlayer from './pages/VideoPlayer';
import TrainingDocument from './pages/TrainingDocument';
import AdminVideoPortal from './pages/AdminVideoPortal';
import AdminAssignments from './pages/AdminAssignments';
import AdminAssignmentBoard from './pages/AdminAssignmentBoard';
import AdminQuestionBank from './pages/AdminQuestionBank';
import { testsWorkspaceRoutes } from './testsWorkspace/routes';
import { AI_TEST_GENERATION_ENABLED, TESTS_WORKSPACE_ENABLED } from './config/features';
import { resolveDocumentTitle } from './utils/documentTitle';
import { PlatformGuideProvider } from './components/PlatformGuide';
import './App.css';

const DocumentTitle: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    document.title = `${resolveDocumentTitle(pathname)} · FEBEST Testing`;
  }, [pathname]);

  return null;
};

const App: React.FC = () => {
  if (process.env.NODE_ENV === 'development') {
    console.log('[APP] render root');
  }
  return (
    <LanguageProvider>
      <AuthProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <PlatformGuideProvider>
        <DocumentTitle />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/tests"
            element={
              <PrivateRoute>
                <TestList />
              </PrivateRoute>
            }
          />
          <Route
            path="/tests/:testId"
            element={
              <PrivateRoute>
                <TestEntryPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/results"
            element={
              <PrivateRoute>
                <Results />
              </PrivateRoute>
            }
          />
          <Route path="/videos" element={<PrivateRoute><VideoPortal /></PrivateRoute>} />
          <Route path="/videos/folders/:folderId" element={<PrivateRoute><VideoFolder /></PrivateRoute>} />
          <Route path="/videos/documents/:documentId" element={<PrivateRoute><TrainingDocument /></PrivateRoute>} />
          <Route path="/videos/:videoId" element={<PrivateRoute><VideoPlayer /></PrivateRoute>} />
          <Route
            path="/admin"
            element={
              <PrivateRoute adminOnly>
                <AdminPanel />
              </PrivateRoute>
            }
          />
          {TESTS_WORKSPACE_ENABLED ? (
            testsWorkspaceRoutes()
          ) : (
            <Route
              path="/admin/tests"
              element={<Navigate to="/admin/board" replace />}
            />
          )}
          <Route
            path="/admin/news"
            element={
              <PrivateRoute adminOnly>
                <AdminPanel initialTab="news" />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <PrivateRoute adminOnly>
                <AdminPanel initialTab="users" />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/mail"
            element={<PrivateRoute adminOnly><AdminPanel initialTab="mail" /></PrivateRoute>}
          />
          <Route
            path="/admin/statistics"
            element={
              <PrivateRoute adminOnly>
                <AdminPanel initialTab="statistics" />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/dashboards"
            element={
              <PrivateRoute adminOnly>
                <AdminPanel initialTab="dashboards" />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/questions"
            element={
              <PrivateRoute adminOnly>
                <AdminQuestionBank />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/board"
            element={
              <PrivateRoute adminOnly>
                <AdminAssignmentBoard />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/assignments"
            element={
              <PrivateRoute adminOnly>
                <AdminAssignments />
              </PrivateRoute>
            }
          />
          {/* Генерация через AI выключена флагом AI_TEST_GENERATION_ENABLED. */}
          {AI_TEST_GENERATION_ENABLED && (
          <Route
              path="/admin/ai-generator"
              element={
                <PrivateRoute adminOnly>
                  <AITestGenerator />
                </PrivateRoute>
              }
            />
          )}
          <Route
            path="/admin/videos"
            element={<PrivateRoute adminOnly><AdminVideoPortal /></PrivateRoute>}
          />
          <Route
            path="/admin/tests/:testId/questions"
            element={
              <PrivateRoute adminOnly>
                <TestQuestions />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/auto-brand"
            element={
              <PrivateRoute>
                <AutoBrandBuilder />
              </PrivateRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </PlatformGuideProvider>
      </Router>
      </AuthProvider>
    </LanguageProvider>
  );
};

export default App;
